import apiClient from "./api";

export interface TimeSeriesDataPoint {
  timestamp: string;
  count: number;
}

export interface DataSeries {
  label: string;
  dataPoints: TimeSeriesDataPoint[];
}

export interface DashboardTimeSeriesResponse {
  series: DataSeries[];
}

export interface EndpointStats {
  toolName: string;
  endpoint: string;
  successCount: number;
  errorCount: number;
  totalCount: number;
}

export interface DashboardEndpointStatsResponse {
  endpoints: EndpointStats[];
}

export interface SkillUsageStats {
  skillId: string;
  skillName: string;
  source: string;
  usageCount: number;
}

export interface DashboardSkillUsageResponse {
  skills: SkillUsageStats[];
}

export interface AutonomousAgentUsageStats {
  agentId: string;
  agentName: string;
  totalRuns: number;
  completed: number;
  failed: number;
}

export interface DashboardAutonomousAgentUsageResponse {
  agents: AutonomousAgentUsageStats[];
}

export interface SecurityViolationStats {
  timestamp: string;
  violationCount: number;
}

export interface SecurityViolationStatsResponse {
  violationsByTime: SecurityViolationStats[];
  totalViolations: number;
  averageViolationsPerDay: number;
}

export interface EngagementFunnelStage {
  stageName: string;
  count: number;
  conversionRate: number;
  overallConversionRate: number;
}

export interface EngagementFunnelResponse {
  stages: EngagementFunnelStage[];
}

export interface SessionQuality {
  averageSessionDurationMinutes: number;
  averageMessagesPerSession: number;
  bounceRate: number;
  resolutionRate: number;
  averageMessagesToFirstTool: number;
  totalSessions: number;
  activeSessions: number;
}

export interface SessionQualityResponse {
  quality: SessionQuality;
}

export enum CustomerHealthStatus {
  Healthy = 0,
  AtRisk = 1,
  Churning = 2,
}

export interface CustomerHealth {
  customerExternalId: string;
  /** Display name from the Customer record; null when never set. Preferred over the ID in the UI. */
  customerName?: string | null;
  /** Email from the Customer record; null when never set. Fallback when no name is set. */
  customerEmail?: string | null;
  healthScore: number;
  status: CustomerHealthStatus;
  lastActivityAt: string | null;
  daysSinceLastActivity: number;
  successRate: number;
  totalSessions: number;
  totalMessages: number;
  violationCount: number;
  authFailureCount: number;
  riskFactors: string[];
}

export interface CustomerHealthResponse {
  customers: CustomerHealth[];
  healthyCount: number;
  atRiskCount: number;
  churningCount: number;
}

export interface FeedbackDailyTrendPoint {
  date: string;
  positiveCount: number;
  negativeCount: number;
}

export interface FeedbackNegativeReasonPoint {
  reasonTag: string;
  count: number;
}

export interface CustomerSessionStats {
  totalSessions: number;
  totalMessages: number;
  totalToolsExecuted: number;
  toolSuccessRate: number;
  averageMessagesPerSession: number;
  lastActiveAt: string | null;
  daysSinceLastActivity: number;
}

export interface CustomerAnalyticsResponse {
  customerId: string;
  funnel: EngagementFunnelStage[];
  sessionStats: CustomerSessionStats;
}

/**
 * Optional explicit from/to window shared by every dashboard chart (from the date-range
 * picker). When both are supplied the backend uses them; otherwise it falls back to `days`/`hours`.
 * ISO 8601 UTC strings, e.g. "2026-06-01T00:00:00.000Z".
 */
export interface DashboardRangeParams {
  startDate?: string;
  endDate?: string;
}

/** Appends startDate/endDate query params when present. */
function rangeQuery(range?: DashboardRangeParams): string {
  if (!range) return "";
  const parts: string[] = [];
  if (range.startDate)
    parts.push(`startDate=${encodeURIComponent(range.startDate)}`);
  if (range.endDate) parts.push(`endDate=${encodeURIComponent(range.endDate)}`);
  return parts.length ? `&${parts.join("&")}` : "";
}

class DashboardService {
  /**
   * Get messages count time series for the last N hours (or explicit range).
   */
  async getMessagesTimeSeries(
    hours: number = 12,
    range?: DashboardRangeParams,
  ): Promise<DashboardTimeSeriesResponse> {
    const response = await apiClient.get<DashboardTimeSeriesResponse>(
      `/dashboard/messages-timeseries?hours=${hours}${rangeQuery(range)}`,
    );
    return response.data;
  }

  /**
   * Get tool executions count time series for the last N hours (or explicit range).
   */
  async getToolExecutionsTimeSeries(
    hours: number = 12,
    range?: DashboardRangeParams,
  ): Promise<DashboardTimeSeriesResponse> {
    const response = await apiClient.get<DashboardTimeSeriesResponse>(
      `/dashboard/tool-executions-timeseries?hours=${hours}${rangeQuery(range)}`,
    );
    return response.data;
  }

  /**
   * Get daily active users count for the last N days (or explicit range).
   */
  async getDailyActiveUsers(
    days: number = 7,
    range?: DashboardRangeParams,
  ): Promise<DashboardTimeSeriesResponse> {
    const response = await apiClient.get<DashboardTimeSeriesResponse>(
      `/dashboard/daily-active-users?days=${days}${rangeQuery(range)}`,
    );
    return response.data;
  }

  /**
   * Get top N endpoints by usage in the last N days (or explicit range).
   */
  async getTopEndpoints(
    days: number = 7,
    topCount: number = 10,
    range?: DashboardRangeParams,
  ): Promise<DashboardEndpointStatsResponse> {
    const response = await apiClient.get<DashboardEndpointStatsResponse>(
      `/dashboard/top-endpoints?days=${days}&topCount=${topCount}${rangeQuery(range)}`,
    );
    return response.data;
  }

  /**
   * Get top N skills by usage (distinct turns) in the last N days (or explicit range)
   */
  async getTopSkills(
    days: number = 7,
    topCount: number = 10,
    range?: DashboardRangeParams,
  ): Promise<DashboardSkillUsageResponse> {
    const response = await apiClient.get<DashboardSkillUsageResponse>(
      `/dashboard/top-skills?days=${days}&topCount=${topCount}${rangeQuery(range)}`,
    );
    return response.data;
  }

  /**
   * Get how often each autonomous agent has been triggered over the last N days.
   */
  async getAutonomousAgentUsage(
    days: number = 7,
    topCount: number = 10,
  ): Promise<DashboardAutonomousAgentUsageResponse> {
    const response = await apiClient.get<DashboardAutonomousAgentUsageResponse>(
      `/dashboard/autonomous-agent-usage?days=${days}&topCount=${topCount}`,
    );
    return response.data;
  }

  /**
   * Get the full list of skills used (uncapped, ordered by usage descending) for the
   * paginated "all skills" table + CSV export. Visible to all authenticated users.
   */
  async getAllSkillsUsage(
    days: number = 7,
    range?: DashboardRangeParams,
  ): Promise<DashboardSkillUsageResponse> {
    const response = await apiClient.get<DashboardSkillUsageResponse>(
      `/dashboard/skills-usage-all?days=${days}${rangeQuery(range)}`,
    );
    return response.data;
  }

  /**
   * Get security violations statistics for the last N days (or explicit range).
   */
  async getSecurityViolations(
    days: number = 7,
    range?: DashboardRangeParams,
  ): Promise<SecurityViolationStatsResponse> {
    const response = await apiClient.get<SecurityViolationStatsResponse>(
      `/dashboard/security-violations?days=${days}${rangeQuery(range)}`,
    );
    return response.data;
  }

  /**
   * Get engagement funnel showing widget load to tool execution conversion
   */
  async getEngagementFunnel(
    days: number = 7,
    range?: DashboardRangeParams,
  ): Promise<EngagementFunnelResponse> {
    const response = await apiClient.get<EngagementFunnelResponse>(
      `/dashboard/engagement-funnel?days=${days}${rangeQuery(range)}`,
    );
    return response.data;
  }

  /**
   * Get session quality metrics
   */
  async getSessionQuality(
    days: number = 7,
    range?: DashboardRangeParams,
  ): Promise<SessionQualityResponse> {
    const response = await apiClient.get<SessionQualityResponse>(
      `/dashboard/session-quality?days=${days}${rangeQuery(range)}`,
    );
    return response.data;
  }

  /**
   * Get customer health scores
   */
  async getCustomerHealth(
    days: number = 30,
    range?: DashboardRangeParams,
  ): Promise<CustomerHealthResponse> {
    const response = await apiClient.get<CustomerHealthResponse>(
      `/dashboard/customer-health?days=${days}${rangeQuery(range)}`,
    );
    return response.data;
  }

  async getFeedbackDailyTrend(
    days: number = 7,
    range?: DashboardRangeParams,
  ): Promise<FeedbackDailyTrendPoint[]> {
    const response = await apiClient.get<FeedbackDailyTrendPoint[]>(
      `/dashboard/feedback-daily-trend?days=${days}${rangeQuery(range)}`,
    );
    return response.data;
  }

  async getNegativeFeedbackByReason(
    days: number = 7,
    range?: DashboardRangeParams,
  ): Promise<FeedbackNegativeReasonPoint[]> {
    const response = await apiClient.get<FeedbackNegativeReasonPoint[]>(
      `/dashboard/feedback-negative-by-reason?days=${days}${rangeQuery(range)}`,
    );
    return response.data;
  }

  async getCustomerAnalytics(
    customerId: string,
    days: number = 30,
    range?: DashboardRangeParams,
  ): Promise<CustomerAnalyticsResponse> {
    const response = await apiClient.get<CustomerAnalyticsResponse>(
      `/dashboard/customers/${encodeURIComponent(customerId)}/analytics?days=${days}${rangeQuery(range)}`,
    );
    return response.data;
  }
}

export const dashboardService = new DashboardService();
